import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    FiUser, FiLock, FiMail, FiCalendar, FiArrowRight, FiArrowLeft,
    FiAlertCircle, FiCheckCircle, FiMessageCircle,
} from 'react-icons/fi';
import { recuperarContaIniciarApi, recuperarContaStatusApi } from '../../Api';
import { notify } from '../../components/Notify';
import useCooldown, { formatarCooldown } from '../../hooks/useCooldown';

// Número de WhatsApp do suporte (com código país+DDD, só dígitos).
// ⚠️ TROCAR pelo número real. Vai pro wa.me/<NUM>?text=<msg>.
const WHATSAPP_SUPORTE = '5511999999999';

function mascaraCpf(v) {
    const d = String(v || '').replace(/\D/g, '').slice(0, 11);
    return d
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

export default function RecuperarConta() {
    const [form, setForm] = useState({
        cpf: '', dataNascimento: '', email: '', senha: '',
    });
    const [erro, setErro] = useState('');
    const [modalErro, setModalErro] = useState(''); // erro de servidor; '' = modal fechado
    const [enviado, setEnviado] = useState(null); // { email_destino }
    const [loading, setLoading] = useState(false);
    const [bloqueio, setBloqueio] = useState(null); // { seconds_left, tier }
    const cd = useCooldown('uen_recuperar_conta_cooldown', 10); // pra enviar de novo
    const navigate = useNavigate();

    // Fecha o modal de erro e volta pro login (comportamento pedido: não fica
    // na tela tentando de novo — o erro é mostrado e o fluxo encerra no login).
    const fecharModalErro = () => {
        setModalErro('');
        navigate('/login');
    };

    // Consulta status de bloqueio quando CPF tem 11 dígitos (UX: já mostra
    // o countdown antes de o user tentar).
    useEffect(() => {
        const digitos = form.cpf.replace(/\D/g, '');
        if (digitos.length !== 11) { setBloqueio(null); return; }
        let ativo = true;
        recuperarContaStatusApi({ cpf: digitos })
            .then((res) => { if (ativo && res.data?.blocked) setBloqueio(res.data); else if (ativo) setBloqueio(null); })
            .catch(() => {});
        return () => { ativo = false; };
    }, [form.cpf]);

    // Tick local pra contagem regressiva quando bloqueado.
    useEffect(() => {
        if (!bloqueio?.seconds_left) return;
        const id = setInterval(() => {
            setBloqueio(b => {
                if (!b) return null;
                const sec = (b.seconds_left || 0) - 1;
                if (sec <= 0) return null;
                return { ...b, seconds_left: sec };
            });
        }, 1000);
        return () => clearInterval(id);
    }, [bloqueio?.seconds_left]);

    const set = (k) => (e) => {
        const v = k === 'cpf' ? mascaraCpf(e.target.value) : e.target.value;
        setForm(f => ({ ...f, [k]: v }));
        if (erro) setErro('');
    };

    const linkWhatsapp = () => {
        const partes = ['Olá! Preciso recuperar minha conta UEN.'];
        if (form.cpf) partes.push(`CPF: ${form.cpf}`);
        const texto = encodeURIComponent(partes.join('\n'));
        return `https://wa.me/${WHATSAPP_SUPORTE}?text=${texto}`;
    };

    const submeter = async (e) => {
        e.preventDefault();
        if (loading || cd.isActive) return;
        const cpfDig = form.cpf.replace(/\D/g, '');
        if (cpfDig.length !== 11) return setErro('Informe um CPF válido.');
        if (!form.dataNascimento) return setErro('Informe a data de nascimento.');
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return setErro('Email inválido.');
        if (form.senha.length < 6) return setErro('Senha precisa de pelo menos 6 caracteres.');

        setLoading(true);
        try {
            const res = await recuperarContaIniciarApi({
                cpf: cpfDig,
                dataNascimento: form.dataNascimento,
                email: form.email,
                senha: form.senha,
            });
            cd.start();
            setEnviado({ email_destino: res.data?.email_destino || form.email });
        } catch (err) {
            const d = err.response?.data;
            let msg;
            if (err.response?.status === 429) {
                setBloqueio({
                    blocked: true,
                    seconds_left: d?.seconds_left || 600,
                    tier: d?.tier || 1,
                });
                msg = 'Muitas tentativas. Aguarde um pouco ou fale com o suporte pelo WhatsApp.';
            } else if (err.response?.status === 409 && d?.error === 'email_em_uso') {
                msg = 'Este email já está vinculado a outra conta.';
            } else if (err.response?.status === 401) {
                msg = 'Dados não conferem. Confira o CPF e a data de nascimento.';
            } else {
                msg = d?.error || d?.message || 'Não foi possível concluir a solicitação.';
            }
            // Erro de servidor → modal (ao fechar volta pro login). As validações
            // de campo (CPF/email/etc.) continuam inline, pra corrigir sem sair.
            setModalErro(msg);
        } finally {
            setLoading(false);
        }
    };

    if (enviado) {
        return (
            <div style={styles.page}>
                <div style={styles.card}>
                    <div style={styles.successIcon}><FiCheckCircle size={36} /></div>
                    <h1 style={styles.title}>Verifique seu email</h1>
                    <p style={styles.subtitle}>
                        Enviamos um link de ativação pra <strong>{enviado.email_destino}</strong>.
                        Clique nele pra ativar sua conta com email e senha.
                    </p>
                    <p style={{ ...styles.subtitle, fontSize: '0.82rem' }}>
                        Link válido por 60 minutos. Não esqueça da caixa de spam.
                    </p>
                    <Link to="/login" style={styles.btnPrimaryLink}>
                        <FiArrowLeft size={14} /> Voltar para o login
                    </Link>
                </div>
            </div>
        );
    }

    const tierLabel = ['', '10 minutos', '1 hora', '24 horas'][bloqueio?.tier || 0] || '';

    return (
        <div style={styles.page}>
            {modalErro && (
                <div style={styles.modalOverlay} onClick={fecharModalErro}>
                    <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalIcon}><FiAlertCircle size={34} /></div>
                        <h2 style={styles.modalTitle}>Não foi possível recuperar</h2>
                        <p style={styles.modalMsg}>{modalErro}</p>
                        <button type="button" style={styles.modalBtn} onClick={fecharModalErro}>
                            <FiArrowLeft size={15} /> Voltar para o login
                        </button>
                    </div>
                </div>
            )}

            <div style={styles.card}>
                <div style={styles.brand}>
                    <div style={styles.brandLogo}>U</div>
                    <div style={styles.brandText}>
                        <span style={styles.brandName}>UEN</span>
                        <span style={styles.brandTag}>Estudantil</span>
                    </div>
                </div>

                <h1 style={styles.title}>Recuperar minha conta</h1>
                <p style={styles.subtitle}>
                    Se você é um aluno antigo e nunca cadastrou email, informe os dados
                    abaixo. Vamos enviar um link de ativação pro email que você escolher.
                </p>

                {bloqueio && (
                    <div style={styles.bloqueioBox}>
                        <FiAlertCircle size={18} />
                        <div style={{ flex: 1 }}>
                            <strong>Conta temporariamente bloqueada.</strong><br />
                            Liberação em <strong>{formatarCooldown(bloqueio.seconds_left)}</strong>.
                            {tierLabel && <span style={{ fontSize: '0.78rem', display: 'block', marginTop: 2 }}>Tier {bloqueio.tier} — bloqueio de {tierLabel}</span>}
                        </div>
                    </div>
                )}

                <form onSubmit={submeter} noValidate>
                    <Campo icon={FiUser} label="CPF">
                        <input
                            value={form.cpf}
                            onChange={set('cpf')}
                            style={styles.input}
                            placeholder="000.000.000-00"
                            inputMode="numeric"
                            maxLength={14}
                            disabled={loading}
                        />
                    </Campo>
                    <Campo icon={FiCalendar} label="Data de nascimento">
                        <input
                            type="date"
                            value={form.dataNascimento}
                            onChange={set('dataNascimento')}
                            style={styles.input}
                            disabled={loading}
                        />
                    </Campo>
                    <Campo icon={FiMail} label="Email que você quer cadastrar">
                        <input
                            type="email"
                            value={form.email}
                            onChange={set('email')}
                            style={styles.input}
                            placeholder="seu@email.com"
                            disabled={loading}
                        />
                    </Campo>
                    <Campo icon={FiLock} label="Crie uma senha">
                        <input
                            type="text"
                            value={form.senha}
                            onChange={set('senha')}
                            style={styles.input}
                            placeholder="mínimo 6 caracteres"
                            disabled={loading}
                        />
                    </Campo>

                    {erro && (
                        <div style={styles.erro}>
                            <FiAlertCircle size={14} /> {erro}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading || cd.isActive || !!bloqueio}
                        style={{ ...styles.btn, opacity: (loading || cd.isActive || bloqueio) ? 0.6 : 1 }}
                    >
                        {loading ? (
                            <><span style={styles.spinner} /> Enviando…</>
                        ) : cd.isActive ? (
                            `Aguarde ${formatarCooldown(cd.secondsLeft)} pra reenviar`
                        ) : (
                            <>Enviar link de ativação <FiArrowRight size={16} /></>
                        )}
                    </button>
                </form>

                <a href={linkWhatsapp()} target="_blank" rel="noopener noreferrer" style={styles.btnWhats}>
                    <FiMessageCircle size={16} /> Falar com suporte no WhatsApp
                </a>

                <Link to="/login" style={styles.backLink}>
                    <FiArrowLeft size={14} /> Voltar para o login
                </Link>
            </div>
        </div>
    );
}

function Campo({ icon: Icon, label, children }) {
    return (
        <div style={styles.field}>
            <label style={styles.label}><Icon size={13} /> {label}</label>
            {children}
        </div>
    );
}

const styles = {
    page: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 60%, #F0FDFA 100%)',
        display: 'grid', placeItems: 'center', padding: '24px 16px',
        fontFamily: "'Inter', 'Roboto', system-ui, sans-serif", color: '#0F172A',
    },
    card: {
        width: '100%', maxWidth: 460, background: '#FFFFFF', borderRadius: 16,
        boxShadow: '0 24px 48px -16px rgba(15,23,42,0.18), 0 0 0 1px rgba(15,23,42,0.04)',
        padding: '36px 32px',
    },
    brand: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 22 },
    brandLogo: {
        width: 44, height: 44, borderRadius: 12,
        background: 'linear-gradient(135deg, #059669 0%, #0D9488 100%)',
        color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 18,
    },
    brandText: { display: 'flex', flexDirection: 'column', lineHeight: 1.1 },
    brandName: { fontWeight: 700, fontSize: '1.1rem', color: '#0F172A' },
    brandTag: { fontSize: '0.75rem', color: '#64748B', marginTop: 2 },

    title: { fontSize: '1.45rem', fontWeight: 800, color: '#0F172A', margin: '0 0 8px' },
    subtitle: { color: '#64748B', margin: '0 0 22px', fontSize: '0.9rem', lineHeight: 1.55 },

    bloqueioBox: {
        display: 'flex', alignItems: 'flex-start', gap: 10,
        background: '#FFF1F2', border: '1px solid #FECDD3',
        borderLeft: '4px solid #E11D48', color: '#9F1239',
        padding: '12px 14px', borderRadius: 10, marginBottom: 18, fontSize: '0.86rem',
    },

    field: { marginBottom: 14 },
    label: {
        display: 'inline-flex', alignItems: 'center', gap: 5,
        fontSize: '0.78rem', fontWeight: 600, color: '#334155', marginBottom: 5,
    },
    input: {
        width: '100%', padding: '11px 12px', border: '1.5px solid #E2E8F0',
        borderRadius: 10, fontSize: '0.92rem', color: '#0F172A', background: '#fff',
        outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
    },
    erro: {
        marginBottom: 12, fontSize: '0.82rem', color: '#9F1239',
        background: '#FFF1F2', padding: '9px 12px', border: '1px solid #FECDD3',
        borderRadius: 8, display: 'flex', alignItems: 'center', gap: 6,
    },

    btn: {
        width: '100%', padding: '12px', marginTop: 6,
        background: 'linear-gradient(135deg, #059669, #10B981)', color: '#fff',
        border: 'none', borderRadius: 10, fontSize: '0.95rem', fontWeight: 700,
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        fontFamily: 'inherit', boxShadow: '0 4px 12px -2px rgba(5,150,105,0.4)',
    },
    spinner: {
        width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)',
        borderTopColor: '#fff', borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    btnWhats: {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
        marginTop: 12, width: '100%', boxSizing: 'border-box', padding: '11px',
        background: '#25D366', color: '#fff', borderRadius: 10, textDecoration: 'none',
        fontSize: '0.9rem', fontWeight: 700,
    },

    successIcon: {
        width: 72, height: 72, borderRadius: '50%',
        background: '#ECFDF5', color: '#059669', display: 'grid', placeItems: 'center',
        margin: '0 auto 16px',
    },
    btnPrimaryLink: {
        display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 14,
        padding: '11px 22px', background: 'linear-gradient(135deg,#059669,#10B981)',
        color: '#fff', borderRadius: 10, textDecoration: 'none', fontWeight: 700, fontSize: '0.92rem',
    },
    backLink: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        marginTop: 18, color: '#059669', fontSize: '0.86rem',
        fontWeight: 600, textDecoration: 'none',
    },

    modalOverlay: {
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(2px)',
        display: 'grid', placeItems: 'center', padding: '24px 16px',
    },
    modalCard: {
        width: '100%', maxWidth: 380, background: '#FFFFFF', borderRadius: 16,
        boxShadow: '0 24px 48px -16px rgba(15,23,42,0.35)',
        padding: '32px 28px', textAlign: 'center',
    },
    modalIcon: {
        width: 64, height: 64, borderRadius: '50%',
        background: '#FFF1F2', color: '#E11D48',
        display: 'grid', placeItems: 'center', margin: '0 auto 16px',
    },
    modalTitle: { fontSize: '1.2rem', fontWeight: 800, color: '#0F172A', margin: '0 0 8px' },
    modalMsg: { color: '#64748B', margin: '0 0 22px', fontSize: '0.92rem', lineHeight: 1.55 },
    modalBtn: {
        width: '100%', padding: '12px', background: 'linear-gradient(135deg,#059669,#10B981)',
        color: '#fff', border: 'none', borderRadius: 10, fontSize: '0.92rem', fontWeight: 700,
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
        fontFamily: 'inherit', boxShadow: '0 4px 12px -2px rgba(5,150,105,0.4)',
    },
};
